import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Clock, Trash2, X } from 'lucide-react';
import { adminApi, ApiError, type AccountDeletionRequestPublic } from '../../api';
import { MetricGrid, SectionHeader, StatCard, confirmPermanentDelete } from '../shared';

export function DeletionRequestsSection({ onChanged }: { onChanged?: () => void }) {
  const [rows, setRows] = useState<AccountDeletionRequestPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const [busyId, setBusyId] = useState('');
  const [rejectId, setRejectId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.listDeletionRequests(filter);
      setRows(res.requests);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load deletion requests.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    void load();
  }, [load]);

  const pendingCount = useMemo(() => rows.filter((r) => r.status === 'pending').length, [rows]);

  const approve = async (row: AccountDeletionRequestPublic) => {
    const msg = `Permanently delete workspace "${row.orgName}"?\n\nThis removes all users, loans, finance records, and settings. This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setBusyId(row.id);
    try {
      const res = await adminApi.approveDeletionRequest(row.id);
      window.alert(`Workspace "${res.orgName || row.orgName}" has been deleted.`);
      await load();
      onChanged?.();
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : 'Approval failed.');
    } finally {
      setBusyId('');
    }
  };

  const reject = async () => {
    if (!rejectId) return;
    const note = rejectNote.trim();
    if (note.length < 4) {
      window.alert('Enter a short rejection reason (at least 4 characters).');
      return;
    }
    setBusyId(rejectId);
    try {
      await adminApi.rejectDeletionRequest(rejectId, note);
      setRejectId(null);
      setRejectNote('');
      await load();
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : 'Rejection failed.');
    } finally {
      setBusyId('');
    }
  };

  const removeRecord = async (row: AccountDeletionRequestPublic) => {
    if (!confirmPermanentDelete('deletion request record', row.orgName)) return;
    setBusyId(row.id);
    try {
      await adminApi.deleteDeletionRequest(row.id);
      await load();
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : 'Failed to delete record.');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Account deletion requests"
        subtitle="Review workspace deletion requests from organization owners. Approving permanently removes the tenant and all data."
      />

      <MetricGrid>
        <StatCard
          label="Pending review"
          value={filter === 'pending' ? rows.length : pendingCount}
          tone="warning"
          icon={<Clock className="h-4 w-4" />}
          hint="Awaiting super-admin action"
        />
        <StatCard label="Listed" value={rows.length} tone="default" icon={<Trash2 className="h-4 w-4" />} hint="Current filter" />
      </MetricGrid>

      <div className="flex flex-wrap gap-2">
        {(['pending', 'all'] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${
              filter === f
                ? 'border-emerald-600 bg-emerald-600 text-white'
                : 'border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50'
            }`}
          >
            {f === 'pending' ? 'Pending' : 'All requests'}
          </button>
        ))}
        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-semibold text-zinc-700 hover:bg-zinc-50"
        >
          Refresh
        </button>
      </div>

      {error ? <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">{error}</div> : null}

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-md">
        <div className="border-b border-zinc-100 bg-zinc-50/90 px-4 py-3">
          <h3 className="text-sm font-semibold text-zinc-900">Deletion queue</h3>
        </div>
        {loading ? (
          <p className="px-4 py-8 text-center text-sm text-zinc-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-zinc-500">No deletion requests in this view.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-100/80 text-[11px] font-bold uppercase tracking-wide text-zinc-500">
                <tr>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Requester</th>
                  <th className="px-4 py-3">Reason</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-zinc-50/90">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-zinc-900">{row.orgName}</p>
                      <p className="text-xs text-zinc-500">{row.orgAdminEmail}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-zinc-800">{row.requesterName || '—'}</p>
                      <p className="text-xs text-zinc-500">{row.requesterEmail}</p>
                    </td>
                    <td className="max-w-xs px-4 py-3 text-xs text-zinc-600">{row.reason}</td>
                    <td className="px-4 py-3 capitalize">{row.status.replace('_', ' ')}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-zinc-500">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.status === 'pending' ? (
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            disabled={busyId === row.id}
                            onClick={() => void approve(row)}
                            className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" />
                            Approve & delete
                          </button>
                          <button
                            type="button"
                            disabled={busyId === row.id}
                            onClick={() => {
                              setRejectId(row.id);
                              setRejectNote('');
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-rose-300 bg-rose-50 px-3 py-1.5 text-xs font-bold text-rose-900 hover:bg-rose-100 disabled:opacity-50"
                          >
                            <X className="h-3.5 w-3.5" />
                            Reject
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={busyId === row.id}
                          onClick={() => void removeRecord(row)}
                          className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 py-1.5 text-xs font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Remove record
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {rejectId ? (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-zinc-950/60 p-4 sm:items-center">
          <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-5 shadow-2xl">
            <h3 className="text-base font-semibold text-zinc-900">Reject deletion request</h3>
            <p className="mt-1 text-sm text-zinc-600">The organization owner will see this note in Settings.</p>
            <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-zinc-500">Reason</label>
            <textarea
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm focus:border-rose-400/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/15"
              placeholder="e.g. Outstanding invoice — contact billing first"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectId(null)}
                className="rounded-lg border border-zinc-200 px-4 py-2 text-sm font-semibold text-zinc-700 hover:bg-zinc-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void reject()}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
              >
                Reject request
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
